var _notificationListQuery;
import { differenceInHours, differenceInMinutes, formatDistanceStrict } from "date-fns";
import React, { useMemo } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import { parseRouteType, useSdkContext } from "~/components/contexts";
import { useReadingHistory } from "~/components/dashboard-widget/display/reading-history-panel/hooks";
import { formatInTimeZone } from "~/lib/utils";
import { useNotificationHistory } from "./hooks";
var NotificationIcon = function NotificationIcon(props) {
  return /*#__PURE__*/React.createElement("svg", props, /*#__PURE__*/React.createElement("path", {
    d: "M15 29.805c1.485 0 2.695-.45 3.633-1.349.78-.74 1.269-1.6 1.464-2.576h6.855c.783 0 1.485-.275 2.11-.821.626-.626.938-1.31.938-2.05v-.412l-.351-.234a15.095 15.095 0 0 1-4.629-5.858c-1.055-2.228-1.582-4.688-1.582-7.383 0-1.875-.626-3.438-1.875-4.688-.899-.781-2.051-1.348-3.458-1.7A2.843 2.843 0 0 0 17.109.8C16.524.293 15.819.04 15 .04c-.82 0-1.524.254-2.109.761a2.844 2.844 0 0 0-.996 1.934c-1.329.35-2.481.918-3.458 1.7-1.249 1.249-1.875 2.812-1.875 4.687 0 2.695-.527 5.175-1.582 7.44a15.2 15.2 0 0 1-4.629 5.801L0 22.598v.41c0 .82.312 1.504.937 2.051.626.546 1.328.82 2.11.82H9.9c.196.976.684 1.836 1.466 2.577.938.9 2.148 1.349 3.633 1.349Z",
    fill: "#E9EEF2"
  }));
};
NotificationIcon.defaultProps = {
  width: "30",
  height: "30",
  viewBox: "0 0 30 30",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
};
import { Container, Content, CreatedDate, EmptyIcon, EmptyMessage, Header, Item } from "./styles";
export const NotificationList = ({
  queryReference
}) => {
  const {
    currentPageType,
    handleRoutePush,
    handleTrackGA,
    handleTrackGA4
  } = useSdkContext();
  const data = usePreloadedQuery(query, queryReference);
  const {
    addNotificationHistory,
    notificationHistory
  } = useNotificationHistory();
  const {
    readingHistory
  } = useReadingHistory();
  const handleItemClick = async ({
    augmentedUrl,
    node
  }) => {
    var _node$content$entityI, _node$content, _node$content2, _node$content3, _node$content4, _node$content5;
    addNotificationHistory(node.entityUuid);
    if (!node.url) return;
    handleTrackGA({
      action: "Article recommend/Notification/Click",
      category: "Article recommend - Notification centre",
      label: node.url
    });
    handleTrackGA4({
      action: "click",
      category: "noticenter",
      customized_parameters: {
        article_id: (_node$content$entityI = (_node$content = node.content) === null || _node$content === void 0 ? void 0 : _node$content.entityId) !== null && _node$content$entityI !== void 0 ? _node$content$entityI : "",
        highlight: "non-highlight"
      },
      subcategory: "article"
    });
    await handleRoutePush(augmentedUrl, {
      applicationId: (_node$content2 = node.content) === null || _node$content2 === void 0 || (_node$content2 = _node$content2.application) === null || _node$content2 === void 0 ? void 0 : _node$content2.entityUuid,
      entityId: (_node$content3 = node.content) === null || _node$content3 === void 0 ? void 0 : _node$content3.entityId,
      entityUuid: (_node$content4 = node.content) === null || _node$content4 === void 0 ? void 0 : _node$content4.entityUuid,
      type: parseRouteType((_node$content5 = node.content) === null || _node$content5 === void 0 ? void 0 : _node$content5.__typename)
    });
  };
  const readingHistorySet = useMemo(() => new Set(readingHistory.map(item => item.entityUuid)), [readingHistory]);
  const notificationHistories = useMemo(() => data.pushNotifications.edges.filter(({
    node
  }) => {
    const hasAlreadyRead = notificationHistory.has(node.entityUuid) || readingHistorySet.has(node.entityUuid);
    return !hasAlreadyRead;
  }).map(item => {
    const computeAugmentedUrl = () => {
      if (!item.node.url) return "";
      try {
        const url = new URL(item.node.url);
        url.searchParams.set("module", "notification_centre");
        url.searchParams.set("pgtype", currentPageType);
        url.protocol = location.protocol;
        url.host = location.host;
        return url.toString();
      } catch {
        return item.node.url;
      }
    };
    return {
      ...item,
      augmentedUrl: computeAugmentedUrl()
    };
  }), [currentPageType, data.pushNotifications.edges, notificationHistory, readingHistorySet]);
  const formatCreatedDate = date => {
    if (!date) return "";
    const now = Date.now();
    const hourDifference = differenceInHours(now, date);
    const minuteDifference = differenceInMinutes(now, date);
    if (minuteDifference === 0) {
      return "1 minute ago";
    }
    if (hourDifference === 0) {
      return `${formatDistanceStrict(now, date, {
        unit: "minute"
      })} ago`;
    }
    if (hourDifference <= 6) {
      return `${formatDistanceStrict(now, date, {
        unit: "hour"
      })} ago`;
    }
    return formatInTimeZone(date);
  };
  return /*#__PURE__*/React.createElement(Container, {
    "data-qa": "notification-list"
  }, notificationHistories.length > 0 ? notificationHistories.map(item => {
    var _item$node;
    return /*#__PURE__*/React.createElement(Item, {
      href: item.augmentedUrl,
      key: item.node.entityUuid,
      onClick: event => {
        event.preventDefault();
        void handleItemClick(item);
      }
    }, /*#__PURE__*/React.createElement(Header, null, item.node.alertTopicDisplay), /*#__PURE__*/React.createElement(Content, null, item.node.pushBody), /*#__PURE__*/React.createElement(CreatedDate, null, formatCreatedDate((_item$node = item.node) === null || _item$node === void 0 ? void 0 : _item$node.createdDate)));
  }) : /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(EmptyMessage, null, "You are up-to-date with all our latest notifications."), /*#__PURE__*/React.createElement(EmptyIcon, null, /*#__PURE__*/React.createElement(NotificationIcon, null))));
};
NotificationList.displayName = "NotificationList";
export const query = _notificationListQuery !== void 0 ? _notificationListQuery : (_notificationListQuery = require("../../../../../queries/__generated__/notificationListQuery.graphql"), _notificationListQuery.hash && _notificationListQuery.hash !== "2b252c37e35edefce75cd6931c12490f" && console.error("The definition of 'notificationListQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _notificationListQuery);